'use client';

import { FunctionComponent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { GetSupportersListNextPageQuery } from '../../lib/graphql';
import { LoadingButton } from '../LoadingButton/LoadingButton';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import { SupporterDetails } from '../SupporterDetails/SupporterDetails';
import classNames from 'classnames';
import { executeQuery } from '../../client/graphql';
import { formatNumber } from '../../utils/formatNumber';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import styles from './SupportersList.module.scss';
import { DonatePopupButton } from '../DonatePopupButton/DonatePopupButton';
import { DonateToGroupMemberButton } from '../DonateToGroupMemberButton/DonateToGroupMemberButton';
import { Page } from '../../lib/pageData';

const supportersListQuery = /* GraphQL */ `
  query getSupportersListNextPage($pageId: ID, $after: String!) {
    page(type: ONE_PAGE, id: $pageId) {
      donations(first: 5, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          legacyId
          creationDate
          amount {
            value
            currencyCode
          }
          giftAidAmount {
            value
            currencyCode
          }
          displayName
          avatar
          message
        }
      }
    }
  }
`;

type SupportersListProps = {
  page: Page;
};

export const SupportersList: FunctionComponent<SupportersListProps> = ({
  page,
}) => {
  const supporters = page.donations?.nodes ?? [];
  const supporterCount =
  page.product?.ownerType === 'PEER_GROUP' // Group pages don't support direct donations. Display members count instead of direct donation count
    ? page.donationSummary?.onlineDonationCount ?? 0
    : page.donationSummary?.donationCount ?? 0;
  const hasNextPage = page.donations?.pageInfo.hasNextPage ?? false;
  const endCursor = page.donations?.pageInfo.endCursor ?? null;
  const nextPageCursor = hasNextPage && endCursor ? endCursor : null;

  const ownerName = page.owner?.name ?? '';

  const { t } = useTranslation();
  const [currentNextPageCursor, setCurrentNextPageCursor] = useState<
    string | null
  >(null);
  const [displayedSupporters, setDisplayedSupporters] = useState(supporters);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    setCurrentNextPageCursor(nextPageCursor); // only set on the client
  }, [nextPageCursor]);

  async function fetchNextPage() {
    setIsLoading(true);
    setHasError(false);

    try {
      const { data, errors } =
        await executeQuery<GetSupportersListNextPageQuery>({
          query: supportersListQuery,
          variables: { pageId: page.legacyId, after: currentNextPageCursor },
          withAutomaticPersistedQueries: true,
        });

      setIsLoading(false);

      if (errors && errors.length > 0) {
        setHasError(true);
      } else {
        const fetchedSupporters = data.page?.donations?.nodes ?? [];
        const hasNextPage = data.page?.donations?.pageInfo.hasNextPage;
        const endCursor = data.page?.donations?.pageInfo.endCursor;

        setCurrentNextPageCursor(hasNextPage && endCursor ? endCursor : null);
        setDisplayedSupporters([...displayedSupporters, ...fetchedSupporters]);
      }
    } catch (e) {
      setIsLoading(false);
      setHasError(true);
      throw e;
    }
  }

  return (
    <section>
      <header>
        <h1 className="cp-heading-small mt-0 mb-3">
          {t('supporters.title')}
          {supporterCount > 0 && (
            <span className={styles.count} data-testid="supporters-count">
              {formatNumber(supporterCount)}
            </span>
          )}
        </h1>
      </header>
      <ul className="m-0 mb-3 p-0">
        {displayedSupporters.map(
          ({
            avatar,
            amount,
            giftAidAmount,
            message,
            creationDate,
            displayName,
            legacyId,
          }) => (
            <SupporterDetails
              key={legacyId}
              date={creationDate}
              userName={displayName}
              image={avatar ? makePhotoUrl(avatar, SIZE_136_136) : undefined}
              message={message}
              currencyCode={amount?.currencyCode ?? 'GBP'}
              donationAmount={(amount?.value ?? 0) / 100}
              giftAidAmount={(giftAidAmount?.value ?? 0) / 100}
            />
          ),
        )}
      </ul>
      {supporterCount === 0 && (
        <>
          <p className="cp-body-medium">
            {t('fundraising.noSupportersMessage', { name: ownerName })}
          </p>
          <SupporterDetailsPlaceholder />
        </>
      )}
      {currentNextPageCursor && (
        <LoadingButton
          className="cp-btn cp-btn-secondary w-full mb-3"
          loading={isLoading}
          onClick={() => fetchNextPage()}
        >
          <Trans i18nKey="supporters.showMore">
            Show more
            <span className="cp-sr-only"> supporters</span>
          </Trans>
        </LoadingButton>
      )}
      {page.product?.ownerType !== 'PEER_GROUP' ? (
        <DonatePopupButton id="supporters-donate-button" />
      ) : (
        <DonateToGroupMemberButton
          page={page}
          id="donate-to-the-group-member"
        />
      )}
      {hasError && (
        <p className={styles.error}>{t('supporters.showMoreError')}</p>
      )}
    </section>
  );
};

const SupporterDetailsPlaceholder: FunctionComponent = () => {
  return (
    <SupporterDetails
      userName={
        <PlaceholderBar width="50%" color="grey-300" marginBottom="28px" />
      }
      message={
        <>
          <PlaceholderBar />
          <PlaceholderBar width="80%" marginBottom="26px" />
          <PlaceholderBar width="60%" marginBottom="17px" />
        </>
      }
    />
  );
};

type PlaceholderBarProps = {
  color?: string;
  width?: string;
  marginBottom?: string;
};

const PlaceholderBar: FunctionComponent<PlaceholderBarProps> = ({
  color = 'neutral-600',
  width = '100%',
  marginBottom = '15px',
}) => {
  return (
    <span
      className={classNames(styles.placeholderBar, `bg-color-${color}`)}
      style={{ width, marginBottom }}
      aria-hidden
    />
  );
};
